import React from "react";
import type {
  FieldSchema,
  JSONFieldSchema,
  LookupFieldSchema,
  RowState
} from "@/components/advanceSearch/types";
import { useFormContext } from "react-hook-form";
import { ConditionTextInput } from "./types/ConditionTextInput";
import { ConditionNumberInput } from "./types/ConditionNumberInput";
import { ConditionDateInput } from "./types/ConditionDateInput";
import { ConditionMonthInput } from "./types/ConditionMonthInput";
import { ConditionDropdownInput } from "./types/ConditionDropdownInput";
import { ConditionCheckboxInput } from "./types/ConditionCheckboxInput";
import { ConditionLookupInput } from "./types/ConditionLookupInput";
import { ConditionJSONInput } from "./types/ConditionJSONInput";

type Props = {
  row: RowState;
  fields: FieldSchema[];
  onClearValue: (which: "value" | "value2") => void;
  dropdownPortalId?: string;
  shortDateFormat?: string;
};

export const ConditionValue: React.FC<Props> = ({
  row,
  fields,
  onClearValue,
  dropdownPortalId,
  shortDateFormat
}) => {
  const { control } = useFormContext();
  const fieldSchema = fields.find((f) => f.name === row.fieldName);
  const fieldType = fieldSchema?.type ?? "text";

  switch (fieldType) {
    case "number":
      return <ConditionNumberInput row={row} control={control} onClear={onClearValue} />;
    case "date":
    case "datetime":
      return (
        <ConditionDateInput
          row={row}
          control={control}
          onClear={onClearValue}
          shortDateFormat={shortDateFormat}
        />
      );
    case "datemonth":
      return <ConditionMonthInput row={row} control={control} onClear={onClearValue} />;
    case "dropdown":
      return (
        <ConditionDropdownInput
          row={row}
          control={control}
          fieldSchema={fieldSchema}
          onClear={onClearValue}
        />
      );
    case "checkbox":
      return <ConditionCheckboxInput row={row} control={control} onClear={onClearValue} />;
    case "lookup":
      return (
        <ConditionLookupInput
          row={row}
          control={control}
          onClear={onClearValue}
          fieldSchema={fieldSchema as LookupFieldSchema}
          dropdownPortalId={dropdownPortalId}
        />
      );
    case "json": {
      return (
        <ConditionJSONInput
          row={row}
          control={control}
          onClear={onClearValue}
          fieldSchema={fieldSchema as JSONFieldSchema}
          dropdownPortalId={dropdownPortalId}
        />
      );
    }
    default:
      return <ConditionTextInput row={row} control={control} onClear={onClearValue} />;
  }
};
